import { useEffect, useState } from "react";
import TagInput from "./TagInput";
import { useModal } from "../context/ModalContext";
import {
  getCompanyDetailsById,
  updateCompanyDetail,
} from "../services/companyService";
import { getStructure } from "../services/structureService";
import Loading from "./Loading";
import { Save, ChevronsRight, Loader2 } from "lucide-react";
import { useToast } from "../context/ToastContext";
import FormNavigationButtons from "./FormNavigationButtons";

function CompanyInformationForm({
  companyId,
  onNext,
  versionId,
  isEditable,
  currentStep,
  totalSteps,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    registeredName: "",
    industry: "",
    structure: "",
    yearOfEstablishment: "",
    websiteUrl: "",
    contactNumber: "",
    telephoneFax: "",
    emailAddress: "",
    numberOfEmployees: "",
    products: ["", "", "", ""],
    services: "",
    version: versionId,
  });

  const [initialFormData, setInitialFormData] = useState(null);
  const [disabledFields, setDisabledFields] = useState({});
  const [structureOptions, setStructureOptions] = useState([]);
  const { showModal } = useModal();
  const toast = useToast();
  const [fallbackUsed, setFallbackUsed] = useState(Number(versionId) > 1);

  const isChanged =
    JSON.stringify(formData) !== JSON.stringify(initialFormData);

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);
      const data = await getCompanyDetailsById(companyId);

      const filledFields = {
        name: !!data.name,
        registeredName: !!data.registeredName,
        industry: !!data.industries?.[0]?.value,
        contactNumber: !!data.contacts?.find((c) => c.contactType === 1),
        emailAddress: !!data.contacts?.find((c) => c.contactType === 5),
        websiteUrl: !!data.websiteUrl,
      };

      const formattedData = {
        name: data.name || "",
        registeredName: data.registeredName || "",
        industry: data.industries?.[0]?.value || "",
        structure: data.structure?.value || "",
        yearOfEstablishment: data.yearOfEstablishment || "",
        websiteUrl: data.websiteUrl || "",
        contactNumber:
          data.contacts?.find((c) => c.contactType === 1)?.value || "",
        emailAddress:
          data.contacts?.find((c) => c.contactType === 5)?.value || "",
        numberOfEmployees: data.numberOfEmployees || "",
        telephoneFax:
          data.telephoneFax?.[0] ||
          data.contacts?.find((c) => c.contactType === 3)?.value ||
          "",
        products: data.offerings
          ?.filter((o) => o.offeringType === 1)
          .map((o) => o.name) || ["", "", "", ""],
        services:
          data.offerings
            ?.filter((o) => o.offeringType === 2)
            .map((o) => o.name)
            .join(", ") || "",
        version: data.version || 2,
      };

      setFormData(formattedData);
      setInitialFormData(formattedData);
      setDisabledFields(filledFields);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error("Error loading company data", err);
    }
  };

  const fetchStructures = async () => {
    try {
      const response = await getStructure();
      setStructureOptions(response || []);
    } catch (err) {
      console.error("Error loading structure options", err);
    }
  };

  useEffect(() => {
    fetchCompanyDetails();
    fetchStructures();
  }, [companyId]);

  const isDisabled = (field) => !!disabledFields[field];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const updateProduct = (index, value) => {
    const updated = [...formData.products];
    updated[index] = value;
    setFormData((prev) => ({ ...prev, products: updated }));
  };

  const handleSave = async (showPopup = true, isContinue = false) => {
    try {
      setSaving(true);

      const structureId =
        structureOptions.find((s) => s.value === formData.structure)?.id || 0;

      const payload = {
        companyId: parseInt(companyId),
        version: formData.version,
        websiteUrl: formData.websiteUrl,
        structureId,
        numberOfEmployees: parseInt(formData.numberOfEmployees) || 0,
        yearOfEstablishment: parseInt(formData.yearOfEstablishment) || 0,
        telephoneFax: formData.telephoneFax ? [formData.telephoneFax] : [],
        products: formData.products
          .filter((name) => name.trim() !== "")
          .map((name) => ({ name, offeringType: 1 })),
        services: formData.services
          ? formData.services.split(",").map((name) => ({
              name: name.trim(),
              offeringType: 2,
            }))
          : [],
      };

      const res = await updateCompanyDetail(payload);

      if (!res.status) {
        const errorText = await res.text();
        console.error("PUT failed with:", res.status, errorText);
        throw new Error("Update failed");
      }

      setInitialFormData(formData); // reset comparison baseline
      // if (showPopup) {
      //   showModal(
      //     "Company Updated",
      //     "Company information updated successfully."
      //   );
      // }

      toast.success("Company information updated successfully.");

      if (isContinue && onNext) onNext();
    } catch (err) {
      console.error("Update error:", err);
      showModal("Update Failed", "Failed to update company information.");
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (label, name, disabled = false, inputClassName = "") => (
    <div className={`sm:flex ${inputClassName}`}>
      <label className="block mb-1 font-medium min-w-[150px]">{label}</label>
      <input
        name={name}
        value={formData[name]}
        onChange={handleChange}
        disabled={disabled || !isEditable}
        placeholder={`Enter ${label}`}
        className={`w-full px-4 py-2 border rounded ${
          isEditable ? "" : "bg-gray-100"
        }`}
      />
    </div>
  );

  return loading ? (
    <Loading fullscreen={false} />
  ) : (
    <section className="sm:px-15">
      <div className="bg-white p-6">
        <h2 className="text-xl font-semibold mb-6">Company Information</h2>

        <div className="flex gap-6 mb-6">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="ventureType"
              checked={formData.version === 1}
              disabled={formData.version !== 1 || !isEditable}
              onChange={() => setFormData({ ...formData, version: versionId })}
            />
            New Venture
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="ventureType"
              checked={formData.version !== 1}
              disabled={formData.version === 1 || !isEditable}
              onChange={() => setFormData({ ...formData, version: versionId })}
            />
            Existing Venture
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {renderInput(
            "Company Name",
            "name",
            isDisabled("name"),
            "col-span-2"
          )}
          {renderInput(
            "Registered Name",
            "registeredName",
            isDisabled("registeredName"),
            "col-span-2"
          )}
          {renderInput("Industry", "industry", isDisabled("industry"))}
          <div className="sm:flex">
            <label className="block mb-1 font-medium sm:min-w-[150px]">
              Structure
            </label>
            <select
              name="structure"
              value={formData.structure}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded ${
                isEditable ? "" : "bg-gray-100"
              }`}
              disabled={!isEditable}
            >
              <option value="">Select Structure</option>
              {structureOptions.map((s) => (
                <option key={s.id} value={s.value}>
                  {s.value}
                </option>
              ))}
            </select>
          </div>
          {renderInput("Year of Establishment", "yearOfEstablishment")}
          {renderInput("Website URL", "websiteUrl", isDisabled("websiteUrl"))}
          {renderInput(
            "Contact No",
            "contactNumber",
            isDisabled("contactNumber")
          )}
          {renderInput("Telephone / Fax", "telephoneFax")}
          {renderInput("Email ID", "emailAddress", isDisabled("emailAddress"))}
          {renderInput("No. Of Employees", "numberOfEmployees")}

          <TagInput
            label="Main Products"
            values={formData.products}
            onChange={(tags) =>
              setFormData((prev) => ({ ...prev, products: tags }))
            }
            isEditable={!isEditable}
          />

          <div className="col-span-2 sm:flex">
            <label className="block mb-1 font-medium min-w-[150px]">
              Other Service:
            </label>
            <input
              value={formData.services}
              onChange={(e) =>
                setFormData({ ...formData, services: e.target.value })
              }
              placeholder="Enter Services"
              className={`w-full px-4 py-2 border rounded ${
                isEditable ? "" : "bg-gray-100"
              }`}
              disabled={!isEditable}
            />
          </div>
        </div>

        {/* --- Buttons Section using the new component --- */}
        <FormNavigationButtons
          onNext={onNext}
          handleSaveOnly={() => handleSave(true, false)}
          handleSaveAndContinue={() => handleSave(true, true)}
          isEditable={isEditable}
          isChanged={isChanged}
          saving={saving}
          fallbackUsed={fallbackUsed}
          currentStep={currentStep} // Pass current step
          totalSteps={totalSteps} // Pass total steps
        />
        {/* --- End Buttons Section --- */}

        <div className="flex flex-wrap gap-4 justify-between sm:justify-end mt-8 hidden">
          {isEditable && (
            <button
              type="button"
              onClick={() => handleSave(true, false)}
              disabled={!isChanged || saving}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
                saving || !isChanged
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
              }`}
            >
              {saving ? (
                <Loader2 className="animate-spin w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
          )}

          {isEditable && (
            <button
              type="button"
              onClick={() => handleSave(true, true)}
              disabled={!isChanged || saving}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
                saving || !isChanged
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 text-white cursor-pointer"
              }`}
            >
              {saving ? (
                <Loader2 className="animate-spin w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save & Continue
            </button>
          )}

          <button
            type="button"
            onClick={onNext}
            disabled={isChanged || saving || !fallbackUsed}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
              isChanged || saving || !fallbackUsed
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-purple-600 hover:bg-purple-700 text-white cursor-pointer"
            }`}
          >
            <ChevronsRight className="w-4 h-4" />
            Next
          </button>
        </div>
      </div>
    </section>
  );
}

export default CompanyInformationForm;
